import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Button, Divider, Grid, Modal, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { ItemDetail, MessageInfo, SearchBox } from 'components/commonparts'
import { DataList } from 'components/parts/MasterStockCenterItem'
import { useCenter } from 'hooks/useCenter'
import { useCenterStockItem } from 'hooks/useCenterStockItem'
import { useGridSearch } from 'hooks/useGridSearch'
import { useItem } from 'hooks/useItems'
import { useSupplier } from 'hooks/useSupplier'
import { useTypecode } from 'hooks/useTypecode'
import { DialogWrapper } from 'templates'
import { isSameResponseResult } from 'types/types'

import type { CenterStockItem, GetMstCenterStockItemCenterStockItemGetRequest, ResponseResult } from 'api-client'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-enterprise'
import 'utils/mst.scss'

export interface ItemInfoDisplayProps {
  open: boolean
  onClose: () => void
  trCompanyId?: number
  itemCode?: string | null
  orgCenterId?: string | null
  centerId?: number
}

const EMPTY_CENTER_STOCK_ITEM = {
  list: [],
} as unknown as CenterStockItem

function ItemInfoDisplay({
  open,
  onClose,
  trCompanyId,
  itemCode,
  orgCenterId,
  centerId,
}: ItemInfoDisplayProps) {
  const { loginUserInfo } = useAuthContext()
  const companyId = trCompanyId ?? loginUserInfo?.companyId

  const centerStockGridSearch = useGridSearch({ autoShowOnCtrlF: open })
  const { fetchItems, items } = useItem()
  const {
    fetchCenterStockItemSearch,
    centerstockitemSearch,
    centerstockitemSearchIsLoading,
    centerstockitemSearchResult,
  } = useCenterStockItem()
  const { fetchCenterSearch, centerSearch } = useCenter()
  const { fetchSupplierSearch, supplierSearch } = useSupplier()
  const { fetchTypeCode, typeCode } = useTypecode()

  const [searchParam, setSearchParam] = useState<GetMstCenterStockItemCenterStockItemGetRequest>({
    companyId: companyId ?? loginUserInfo!.companyId,
    centerId: centerId ?? undefined,
    itemCode: itemCode ?? undefined,
  })
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined)
  const [hasGridEdited, setHasGridEdited] = useState(false)
  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)
  const fetchedKeyRef = useRef<string>('')
  const [reloadSeq, setReloadSeq] = useState(0)

  const doFetch = useCallback(async () => {
    if (!companyId || !itemCode) return
    const currentSearchParam: GetMstCenterStockItemCenterStockItemGetRequest = {
      companyId,
      centerId: centerId ?? undefined,
      itemCode,
      enabled: false,
      orgSupplierId: orgCenterId ?? undefined,
    }
    setSearchParam(currentSearchParam)

    await Promise.all([
      fetchItems({
        enabled: false,
        companyId,
        orgSupplierId: currentSearchParam.orgSupplierId ?? undefined,
        itemCode: currentSearchParam.itemCode ?? undefined }),
      fetchCenterStockItemSearch(currentSearchParam as any),
      fetchCenterSearch({ companyId, enabled: false } as any, false),
      fetchSupplierSearch({ companyId, enabled: false } as any, false),
      fetchTypeCode(),
    ])
  }, [companyId, itemCode, centerId, orgCenterId, fetchItems, fetchCenterStockItemSearch, fetchCenterSearch, fetchSupplierSearch, fetchTypeCode])

  useEffect(() => {
    if (centerstockitemSearchResult == null) return
    setResultMessage((prev) => (
      isSameResponseResult(prev, centerstockitemSearchResult) ? prev : centerstockitemSearchResult
    ))
  }, [centerstockitemSearchResult])

  useEffect(() => {
    if (gridMessage == null) return
    setResultMessage((prev) => (isSameResponseResult(prev, gridMessage) ? prev : gridMessage))
  }, [gridMessage])

  useEffect(() => {
    if (!open) {
      fetchedKeyRef.current = ''
      setUpdateTrigger(undefined)
      setHasGridEdited(false)
      setResultMessage(undefined)
      setGridMessage(undefined)
      return
    }
    if (!companyId || !itemCode) return

    const key = `${companyId}|${centerId ?? ''}|${orgCenterId ?? ''}|${itemCode}|${reloadSeq}`
    if (fetchedKeyRef.current === key) return
    fetchedKeyRef.current = key

    doFetch().catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e)
    })
  }, [open, companyId, centerId, orgCenterId, itemCode, reloadSeq, doFetch])

  const currentCenterStock = (centerstockitemSearch ?? EMPTY_CENTER_STOCK_ITEM) as CenterStockItem
  const detailItem = useMemo(
    () => currentCenterStock.list?.find((r) => r.recencyFlg) ?? currentCenterStock.list?.[0],
    [currentCenterStock]
  )

  return (
    <Modal keepMounted open={open} onClose={onClose}>
      <DialogWrapper>
        <Box sx={{ pt: 2 }}>
          <ItemDetail item={detailItem as any} defaultExpanded disableCollapse stockCenterFlg />

          <Divider sx={{ my: 2 }} />

          <Typography component="div" variant="subtitle1" sx={{ fontWeight: 700 }}>
            センター在庫管理
          </Typography>
          <SearchBox
            gridRef={centerStockGridSearch.searchBoxProps.gridRef}
            findSearchValue={centerStockGridSearch.searchBoxProps.findSearchValue}
            setFindSearchValue={centerStockGridSearch.searchBoxProps.setFindSearchValue}
            activeMatchNum={centerStockGridSearch.searchBoxProps.activeMatchNum}
            onFindChanged={centerStockGridSearch.searchBoxProps.onFindChanged}
            isVisible={centerStockGridSearch.searchBoxProps.isVisible}
            onHide={centerStockGridSearch.searchBoxProps.onHide}
          />
          <DataList
            list={currentCenterStock}
            items={items.list}
            center={centerSearch.list}
            supplier={supplierSearch.list}
            systype={typeCode}
            onReload={() => {
              setHasGridEdited(false)
              setReloadSeq((prev) => prev + 1)
            }}
            isLoading={centerstockitemSearchIsLoading}
            searchParam={searchParam}
            updateTrigger={updateTrigger}
            setHasGridEdited={setHasGridEdited}
            setResultMessage={setGridMessage}
            fixedGridHeight="28vh"
            gridRef={centerStockGridSearch.gridRef}
            findSearchValue={centerStockGridSearch.gridSearchProps.findSearchValue}
            onFindChanged={centerStockGridSearch.gridSearchProps.onFindChanged}
            isSearchVisible={centerStockGridSearch.searchBoxProps.isVisible}
          />

          <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 2, gap: 1 }}>
            <MessageInfo result={resultMessage} />
            <Button variant="contained" color="secondary" onClick={() => setUpdateTrigger((prev) => !prev)} disabled={!hasGridEdited}>
              登録
            </Button>
            <Button variant="contained" color="secondary" onClick={onClose}>
              閉じる
            </Button>
          </Grid>
        </Box>
      </DialogWrapper>
    </Modal>
  )
}

export default ItemInfoDisplay

